// Client-side Zod mirror of the server's `printTemplateContentSchema`
// (see `server/src/modules/printTemplates/printTemplates.types.ts`).
//
// The builder validates against this before save so feedback lands
// on the field rather than as a server 400. The shapes must match
// exactly — any divergence is a bug.
//
// Per-type column-key membership is checked separately in
// `validateForType` so the validator can read the per-type registry
// from `./variables.ts`.

import { z } from 'zod';

import type { PrintTemplateType } from '../../../types/api';
import { getColumnKeysForType } from './variables';

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

export const headerLogoSchema = z
  .object({
    show: z.boolean(),
    max_height_mm: z.number().int().min(6).max(30),
  })
  .strict();

export const headerConfigSchema = z
  .object({
    show: z.boolean(),
    logo: headerLogoSchema,
    title_template: z.string().max(500),
    subtitle_template: z.string().max(500),
    company_lines: z.array(z.string().max(500)).max(6),
    alignment: z.enum(['left', 'center', 'right']),
  })
  .strict();

// ---------------------------------------------------------------------------
// Body
// ---------------------------------------------------------------------------

export const columnFormatSchema = z.enum(['plain', 'currency', 'date_short']);

export const columnDefSchema = z
  .object({
    key: z.string().min(1).max(80),
    label: z.string().min(1).max(80),
    width_pct: z.number().min(1).max(60),
    align: z.enum(['left', 'center', 'right']),
    format: columnFormatSchema.optional(),
  })
  .strict();

export const bodyConfigSchema = z
  .object({
    columns: z.array(columnDefSchema).min(0).max(12),
    show_zebra: z.boolean(),
    group_by_day: z.boolean(),
    show_day_subtotals: z.boolean(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const total = value.columns.reduce((acc, c) => acc + c.width_pct, 0);
    if (total > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['columns'],
        message: `Sum of column width_pct (${total}) must not exceed 100`,
      });
    }
  });

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

export const footerConfigSchema = z
  .object({
    show: z.boolean(),
    text_template: z.string().max(2000),
    show_page_numbers: z.boolean(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

// Lower bound mirrored from server: 3mm is the floor so the legacy
// receipt template (3mm @page margin) validates client-side too.
export const pageMarginsSchema = z
  .object({
    top: z.number().int().min(3).max(30),
    right: z.number().int().min(3).max(30),
    bottom: z.number().int().min(3).max(30),
    left: z.number().int().min(3).max(30),
  })
  .strict();

export const pageConfigSchema = z
  .object({
    size: z.enum(['A4', 'A5', 'Letter']),
    orientation: z.enum(['portrait', 'landscape']),
    margins_mm: pageMarginsSchema,
  })
  .strict();

// ---------------------------------------------------------------------------
// Top-level content
// ---------------------------------------------------------------------------

export const renderModeSchema = z.enum(['columns', 'custom_html']);

export const printTemplateContentSchema = z
  .object({
    version: z.literal(1),
    mode: renderModeSchema.optional(),
    header: headerConfigSchema,
    body: bodyConfigSchema,
    footer: footerConfigSchema,
    page: pageConfigSchema,
    custom_html: z.string().max(200_000).optional(),
    custom_css: z.string().max(200_000).optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.mode === 'custom_html') {
      if ((value.custom_html ?? '').trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['custom_html'],
          message: 'custom_html is required when mode is "custom_html"',
        });
      }
    }
  });

// ---------------------------------------------------------------------------
// Per-type validator (column-key membership check)
// ---------------------------------------------------------------------------

export interface ValidationResult {
  ok: boolean;
  /** Field-level error messages keyed by dotted path (matches server). */
  errors: Record<string, string>;
}

/**
 * Run the structural schema THEN the per-type column-key check.
 * Returns a flat `{ ok, errors }` object — the builder maps these
 * onto the relevant editor fields.
 */
export function validateForType(
  type: PrintTemplateType,
  raw: unknown,
): ValidationResult {
  const parsed = printTemplateContentSchema.safeParse(raw);
  if (!parsed.success) {
    const errors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const key = issue.path.length === 0 ? '_' : issue.path.map((p) => String(p)).join('.');
      if (!(key in errors)) errors[key] = issue.message;
    }
    return { ok: false, errors };
  }
  // custom_html mode bypasses column-key validation entirely — the
  // body table is unused. Mirrors the server validator branch.
  if (parsed.data.mode === 'custom_html') {
    if (parsed.data.body.columns.length > 0) {
      return {
        ok: false,
        errors: {
          'body.columns':
            'Columns must be empty when mode is "custom_html" — switch to Visual Builder to define a tabular body.',
        },
      };
    }
    return { ok: true, errors: {} };
  }
  const allowedKeys = new Set(getColumnKeysForType(type).map((c) => c.key));
  if (allowedKeys.size === 0) {
    if (parsed.data.body.columns.length > 0) {
      return {
        ok: false,
        errors: {
          'body.columns': `template_type='${type}' does not yet accept body columns; use diary_results for tabular output`,
        },
      };
    }
    return { ok: true, errors: {} };
  }
  const invalid: string[] = [];
  parsed.data.body.columns.forEach((c) => {
    if (!allowedKeys.has(c.key)) invalid.push(c.key);
  });
  if (invalid.length > 0) {
    return {
      ok: false,
      errors: {
        'body.columns': `Unknown key(s) for template_type='${type}': ${invalid.join(', ')}`,
      },
    };
  }
  return { ok: true, errors: {} };
}
